// ============================================================================
// CW-Q Phase 6 — the two Financial-summary cells that were lying (2026-08-14).
//
// Extracted out of FinancialSummaryCard so they can be rendered and READ in
// isolation. The card itself pulls in FacilityBillingCard → useAuth(), which
// throws outside <AuthProvider>, so the whole card cannot be server-rendered
// without stubbing auth — and a fix nobody can observe is a fix nobody should
// trust ("a green build is not evidence"). These two components own every
// pixel this phase changes, take plain props, use no context, and have a
// server-rendering test beside them.
//
// All the wording and every rule lives in src/shared/rate-basis.ts. Nothing
// here decides anything.
// ============================================================================

import {
  aboutAMonth,
  clientRateDisplay,
  marginAvailability,
  rateBasisChip,
  resumesAtHint,
  scheduleEstimateCaption,
  scheduleMonthlyEstimate,
  type RateBasis,
} from '../../../shared/rate-basis';
import { InlineEditField } from './inline-edit-field';
import type { BilledMonthFigure } from '../lib/billed-months';

/**
 * "Monthly client rate", told the truth.
 *
 * Before: `value={facility.monthly_rate ?? null}` → InlineEditField's
 * `(not set)` placeholder on all 14 hourly hospitals, which is the same thing
 * it says for a building nobody has rated. After: the basis in a chip, what
 * the building actually bills on in the value slot, and why underneath.
 *
 * 2026-08-26, TCSD-0115 step 3 — the changeover. This figure is no longer a
 * number anybody types. It is the SUM of the building's Active service lines,
 * maintained by fn_recompute_facility_rate(), and the database refuses a hand
 * write to it from every role and through the protected-column bypass. So the
 * "Change the rate" affordance is gone: there is nothing here to propose against.
 * A client rate changes by changing the LINE that changed, in the Service lines
 * section below, which has its own per-line propose path.
 */
export function ClientRateRow({
  basis,
  monthlyRate,
  activeLineCount,
  buildingIsLive = true,
  scheduledIncrease = null,
  historyHref = null,
  hourlyRate = null,
  paused = false,
}: {
  basis: RateBasis;
  monthlyRate: number | null;
  /**
   * Items 831/758: the rate of the building's running line billed by the
   * hour (runningHourlyRate). Read only on an hourly building; null there reads
   * just "Billed on actual hours".
   */
  hourlyRate?: number | null;
  /** How many counted service lines this figure is the sum of. */
  activeLineCount: number;
  /**
   * Item 904 — the building is paused. `monthlyRate` is untouched by the pause
   * (TCSD-0076: kept on purpose, as what service resumes at), so it doubles
   * as the resume figure: the cell reads $0.00 now and quotes `monthlyRate`
   * underneath as what it comes back to.
   */
  paused?: boolean;
  /**
   * Whether the building has opened.
   *
   * ⛔ 2026-09-17: this row called the lines "active" whatever the building's
   * status, while the SUB COST row three lines below already said "agreed" for
   * a building that has not opened. Houston Physicians read
   * "Sum of 2 active service lines" and "Sum of 2 agreed service lines" in the
   * same card, about the same two lines, both of them Scheduled, under a
   * heading saying "Starts October 1 — agreed, not yet earning." A line that
   * starts in a fortnight is not active, and a card should not need the reader
   * to decide which of its own two sentences to believe.
   */
  buildingIsLive?: boolean;
  /**
   * An annual increase booked for a future night (item #706). The figure above
   * stays today's until that night; this says what it becomes and when.
   */
  scheduledIncrease?: { on: string; newTotal: number } | null;
  /** Where this building's rate history is read; omitted for roles that cannot read it. */
  historyHref?: string | null;
}) {
  if (paused) {
    // A rate of 0 or less is not a rate (the same guard clientRateDisplay
    // applies on the unpaused path) — it says there is nothing recorded
    // rather than claiming the building resumes to $0.
    const hasRate = monthlyRate != null && monthlyRate > 0;
    return (
      <InlineEditField
        label="Monthly client rate"
        value="$0.00"
        badge="Paused"
        hint={hasRate ? resumesAtHint(monthlyRate)! : 'No rate recorded for when service resumes.'}
        readOnly
        fieldKey="facility_monthly_rate"
      />
    );
  }
  const d = clientRateDisplay({ basis, monthlyRate, hourlyRate });
  const word = buildingIsLive ? 'active' : 'agreed';
  // ⛔ An hourly building's rate is not a sum of anything: its lines add
  // nothing to a monthly rate (items 831/758). It says where the hourly rate
  // is changed instead.
  const source =
    d.basis === 'hourly'
      ? 'To change the hourly rate, change the line.'
      : activeLineCount === 0
        ? `No ${word} service lines — this building has no recurring rate.`
        : `Sum of ${activeLineCount} ${word} service line${activeLineCount === 1 ? '' : 's'}. To change it, change the line.`;
  const field = (
    <InlineEditField
      label={d.basis === 'hourly' ? 'Client rate' : 'Monthly client rate'}
      value={d.text}
      badge={rateBasisChip(d.basis)}
      hint={d.hint ? `${d.hint} ${source}` : source}
      readOnly
      fieldKey="facility_monthly_rate"
    />
  );
  const history = historyHref ? (
    <a href={historyHref} className="mt-1 block text-[11px] text-tcs-text-secondary underline-offset-2 hover:underline">
      Rate history
    </a>
  ) : null;
  if (!scheduledIncrease) {
    return history ? (
      <div>
        {field}
        {history}
      </div>
    ) : (
      field
    );
  }
  const on = new Date(`${scheduledIncrease.on}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const to = scheduledIncrease.newTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  return (
    <div>
      {field}
      <span
        className="mt-1 inline-block rounded-full bg-st-info-bg px-2 py-0.5 text-xs text-st-info-fg-strong"
        data-field="facility_scheduled_increase"
      >
        Increase scheduled {on}: {to}
      </span>
      {history}
    </div>
  );
}

/**
 * Item 904 — the one "this figure is $0 because service is paused" mark, so
 * every cell in the money box that reads $0 while paused (client rate, sub
 * cost, margin) says why the same way.
 */
export function PausedFieldBadge() {
  return (
    <span className="ml-2 inline-flex items-center rounded border border-tcs-border bg-tcs-raised px-1.5 py-0.5 text-[10px] font-medium text-tcs-text-secondary">
      Paused
    </span>
  );
}

/** dt/dd cell matching facility-detail's InfoField, plus a caption. */
function MarginCell({
  label,
  value,
  hint,
  fieldKey,
  paused = false,
}: {
  label: string;
  value: string | null;
  hint: string | null;
  fieldKey: string;
  /** Item 904 — marks a margin read while the building is paused. */
  paused?: boolean;
}) {
  return (
    <div data-field={fieldKey}>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
        {label}
        {paused && <PausedFieldBadge />}
      </dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm text-tcs-text">{value ?? '—'}</dd>
      {hint && <p className="mt-0.5 text-[11px] text-tcs-text-muted">{hint}</p>}
    </div>
  );
}

/**
 * Margin (monthly) + Margin %.
 *
 * Before: both cells rendered a bare '—' for three completely different
 * situations — an hourly building whose revenue is elsewhere, a building with
 * no rate recorded, and a building with no sub cost recorded. FAC-0123 showed
 * $3,150/mo of sub cost next to two dashes, leaving the reader to decide
 * whether that was a bug. Now the dash always comes with the reason.
 */
export function MarginRows({
  basis,
  clientRate,
  subTotal,
  marginMonthly,
  marginPct,
  paused = false,
  resumeMarginMonthly = null,
  resumeMarginPct = null,
}: {
  basis: RateBasis;
  clientRate: number | null;
  subTotal: number | null;
  /** Only read when `marginAvailability` says a margin may be shown. */
  marginMonthly: number | null;
  /** Fraction, not percent: 0.2705 renders as 27.1%. */
  marginPct: number | null;
  /** Item 904 — the building is paused: both cells read "—", marked. */
  paused?: boolean;
  /** What the margin comes to once service resumes — read only while `paused`. */
  resumeMarginMonthly?: number | null;
  resumeMarginPct?: number | null;
}) {
  if (paused) {
    const hint = resumesAtHint(resumeMarginMonthly, resumeMarginPct) ?? 'No figure recorded for when service resumes.';
    return (
      <>
        <MarginCell label="Margin (monthly)" value="—" hint={hint} fieldKey="facility_margin_monthly" paused />
        <MarginCell label="Margin %" value="—" hint={null} fieldKey="facility_margin_pct" paused />
      </>
    );
  }
  const a = marginAvailability({ basis, clientRate, subTotal });
  const money =
    a.available && marginMonthly != null
      ? `$${marginMonthly.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
      : null;
  const pct = a.available && marginPct != null ? `${(marginPct * 100).toFixed(1)}%` : null;
  return (
    <>
      <MarginCell
        label="Margin (monthly)"
        value={money}
        hint={a.reason}
        fieldKey="facility_margin_monthly"
      />
      <MarginCell
        label="Margin %"
        value={pct}
        // The reason is already under the cell to its left; repeating it in the
        // adjacent cell reads as two separate problems.
        hint={null}
        fieldKey="facility_margin_pct"
      />
    </>
  );
}

/**
 * "What the schedule comes to" (item 758, option A). Shown only on a line
 * billed by the hour that has its hours and days set. Worked out here, each
 * time, from the line — never saved, never called a rate.
 */
export function ScheduleEstimateRow({
  rate,
  hoursPerDay,
  daysPerWeek,
}: {
  rate: number | null;
  hoursPerDay: number | null;
  daysPerWeek: number | null;
}) {
  const est = scheduleMonthlyEstimate(rate, hoursPerDay, daysPerWeek);
  if (est == null) return null;
  return (
    <MarginCell
      label="What the schedule comes to"
      value={aboutAMonth(est)}
      hint={scheduleEstimateCaption(rate as number, hoursPerDay as number, daysPerWeek as number)}
      fieldKey="facility_schedule_estimate"
    />
  );
}


function monthName(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function dollars(n: number): string {
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `−$${s}` : `$${s}`;
}

/**
 * "Billed months (before management fee and tax)" (item 758, option A): up to
 * three months, newest first, each named, then their low-to-high range.
 * ⛔ No average anywhere: one averaged figure reads as a rate.
 *
 * `readable` false means the viewer's own rights cannot see billing lines — the
 * block says so in a sentence rather than "None yet" (TCSD-0049: an empty field
 * says why it is empty).
 */
export function BilledMonthsRow({
  months,
  readable,
  loading = false,
  error = null,
}: {
  months: BilledMonthFigure[];
  readable: boolean;
  loading?: boolean;
  error?: string | null;
}) {
  const label = 'Billed months (before management fee and tax)';
  if (!readable) {
    return <MarginCell label={label} value="Billed months are shown to Finance" hint={null} fieldKey="facility_billed_months" />;
  }
  if (error) {
    return (
      <MarginCell
        label={label}
        value="Could not read the billed months"
        hint={error}
        fieldKey="facility_billed_months"
      />
    );
  }
  if (loading) {
    return <MarginCell label={label} value="Reading…" hint={null} fieldKey="facility_billed_months" />;
  }
  if (months.length === 0) {
    return (
      <MarginCell
        label={label}
        value="None yet"
        hint="The last three billed months will appear here as they are billed."
        fieldKey="facility_billed_months"
      />
    );
  }
  const amounts = months.map((m) => m.amount);
  const low = Math.min(...amounts);
  const high = Math.max(...amounts);
  return (
    <div data-field="facility_billed_months">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">{label}</dt>
      <dd className="mt-1 text-sm text-tcs-text">
        <ul className="space-y-0.5">
          {months.map((m) => (
            <li key={m.month} className="flex justify-between gap-4">
              <span>{monthName(m.month)}</span>
              <span className="font-mono">{dollars(m.amount)}</span>
            </li>
          ))}
        </ul>
      </dd>
      {months.length > 1 && (
        <p className="mt-0.5 text-[11px] text-tcs-text-muted">
          Last {months.length === 3 ? 'three' : 'two'} months ranged {dollars(low)} – {dollars(high)}.
        </p>
      )}
    </div>
  );
}
